import {
  type ChatMessages,
  createGenerateSignedUrlRequest,
  useAssetClient,
  useCallOptions,
  withBearerToken,
} from "@storytell/cp-client";
import { StButton } from "@storytell/ui/buttons";
import { TbChevronRight, TbFileText, TbLoader2, TbPhoto, TbSearch } from "solid-icons/tb";
import { Show, For, createSignal } from "solid-js";
import { useWire } from "~/wire";

interface SearchResultsRendererProps {
  searchResults: ChatMessages.SearchResult;
}

interface AssetSummaryItemProps {
  asset: ChatMessages.AssetSummary;
}

const AssetSummaryItem = (props: AssetSummaryItemProps) => {
  const [isDownloading, setIsDownloading] = createSignal(false);
  const wire = useWire();
  const formatFileSize = (bytes: bigint) => {
    const units = ["B", "KB", "MB", "GB"];
    let size = Number(bytes);
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const downloadAsset = async (assetId: string, filename: string) => {
    if (isDownloading()) return; // Prevent double-clicks

    setIsDownloading(true);

    try {
      const client = useAssetClient();
      const request = createGenerateSignedUrlRequest({
        assetId,
      });
      const authToken = wire.auth.token();

      if (!authToken) {
        throw new Error("No authentication token available");
      }

      const callOptions = useCallOptions(withBearerToken(authToken));

      // Get the signed URL
      const response = await client.generateSignedURL(request, callOptions);

      if (response.isOk()) {
        const signedUrl = response.value.signedUrl;

        // Create a temporary link element to trigger download
        const link = document.createElement("a");
        link.href = signedUrl;
        link.download = filename || "download";
        link.target = "_blank";

        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error("Failed to get signed URL:", response.error);
        throw new Error("Failed to get signed URL");
      }
    } catch (error) {
      console.error("Failed to download asset:", error);
      // TODO: Show error toast/notification to user
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      <div class="flex items-start gap-3">
        <div class="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
          <Show
            when={props.asset.type === "image"}
            fallback={<TbFileText class="w-4 h-4 text-blue-600 dark:text-blue-400" />}
          >
            <TbPhoto class="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </Show>
        </div>

        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1">
            <h4 class="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
              {props.asset.title || props.asset.originalFilename}
            </h4>
            <span class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
              {props.asset.type}
            </span>
          </div>

          <Show when={props.asset.originalFilename && props.asset.originalFilename !== props.asset.title}>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate">{props.asset.originalFilename}</p>
          </Show>

          <Show when={props.asset.summary}>
            <p class="text-sm text-gray-700 dark:text-gray-300 mb-2 line-clamp-2">{props.asset.summary}</p>
          </Show>

          <div class="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
            <span>{formatFileSize(props.asset.sizeBytes)}</span>
            <Show when={props.asset.id}>
              <StButton
                noBorder
                noColor
                class="text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => downloadAsset(props.asset.id, props.asset.originalFilename)}
                loading={isDownloading()}
              >
                {isDownloading() ? "Downloading..." : "Download"}
              </StButton>
            </Show>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Renders search results in a structured format instead of markdown
 */
export const SearchResultsRenderer = (props: SearchResultsRendererProps) => {
  const duration = () => {
    const duration = props.searchResults.duration;
    if (!duration) return "0 seconds";

    let totalSeconds: number;
    if (duration.seconds !== undefined) {
      totalSeconds = Number(duration.seconds) + Number(duration.nanos || 0) / 1000000000;
    } else {
      const durationNum = Number(duration);
      totalSeconds = !Number.isNaN(durationNum) ? durationNum : 0;
    }
    // Round to nearest 0.5
    const rounded = Math.round(totalSeconds * 2) / 2;
    const label = rounded === 1 ? "second" : "seconds";
    return `${rounded} ${label}`;
  };

  const [showAssets, setShowAssets] = createSignal(false);

  return (
    <div>
      <div class="flex items-center gap-1 mb-2">
        <TbSearch class="size-4 text-muted-text" />
        <h3 class="text-sm text-secondary-text">
          Searched for <span class="font-medium text-primary-text">"{props.searchResults.query}"</span>
        </h3>
      </div>

      <Show
        when={props.searchResults.results?.case === "assets" && props.searchResults.results.value?.assets?.length > 0}
        fallback={<p class="text-sm text-primary-text italic">No search results found</p>}
      >
        <button
          type="button"
          onClick={() => setShowAssets((prev) => !prev)}
          class="group/btn flex items-center gap-2 mb-2 cursor-pointer"
        >
          <p class="text-sm text-blue-800 dark:text-blue-400 font-medium group-hover/btn:underline">
            Found {props.searchResults.results?.value?.assets?.length} asset
            {props.searchResults.results?.value?.assets?.length !== 1 ? "s" : ""} in {duration()}
          </p>

          <TbChevronRight
            class="size-3.5 stroke-3 text-secondary-text transition-transform"
            classList={{ "rotate-90": showAssets() }}
          />
        </button>

        <Show when={showAssets()}>
          <div class="space-y-3">
            <For each={props.searchResults.results?.value?.assets}>{(asset) => <AssetSummaryItem asset={asset} />}</For>
          </div>
        </Show>
      </Show>
    </div>
  );
};
